import React from 'react'
import { Typography, Chip } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Container } from '../../Container'
import { ChatMessage } from '../../../navigation/layouts/Lori/Channels/LoriChannelContent/ChatMessage'
import {
    ChatMessageContent,
    ConversationType as LoriConversationType,
} from '../../../navigation/layouts/Lori/types'
import { AGENT_DRAWER_WIDTH } from '../../../navigation/constants'
import { TrainingVideoController } from '../../../hooks/useTrainingVideo'
import { ProjectPlanController } from '../../../hooks/useProjectPlan'
import {
    ConversationAnalyticsItem,
    ConversationProblem,
    ChatMessageAnalytics,
} from './analysisTypes'

function toDisplayMessage(msg: ChatMessageAnalytics): ChatMessageContent {
    return {
        id: msg.id,
        created_date: msg.created_date,
        role: msg.role,
        content_type:
            msg.content_type === 'input_text'
                ? 'text'
                : (msg.content_type as ChatMessageContent['content_type']),
        message_items: msg.message_items,
        function_call: msg.function_call ?? undefined,
        function_call_response: msg.function_call_response ?? undefined,
    }
}

interface Props {
    conversation: ConversationAnalyticsItem | null
    trainingVideoController: TrainingVideoController
    projectPlanController: ProjectPlanController
    conversationProblem?: ConversationProblem | null
}

export const ConversationDetailPanel = ({
    conversation,
    trainingVideoController,
    projectPlanController,
    conversationProblem,
}: Props) => {
    const theme = useTheme()

    const isFlagged = conversationProblem != null

    return (
        <Container
            style={{
                width: AGENT_DRAWER_WIDTH,
                height: 'calc(100vh - 260px)',
                overflowY: 'auto',
                padding: theme.spacing(2),
                marginRight: theme.spacing(2),
                borderRadius: '10px',
                border: `1px solid ${theme.palette.divider}`,
                flexDirection: 'column',
            }}
        >
            {conversation ? (
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Container
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            <Typography variant="h6" noWrap>
                                Conversation with {conversation.user.name}
                            </Typography>
                            {isFlagged && (
                                <Chip
                                    label={conversationProblem.category_display}
                                    size="small"
                                    style={{
                                        backgroundColor:
                                            theme.palette.error.main,
                                        color: theme.palette.error.contrastText,
                                    }}
                                />
                            )}
                        </Container>
                    </Container>

                    {conversation.topic_display && (
                        <Typography variant="caption" color="textSecondary">
                            Topic: {conversation.topic_display} | Intent:{' '}
                            {conversation.intent_display ?? 'N/A'}
                        </Typography>
                    )}
                    {conversation.chat_messages.map((message) => (
                        <ChatMessage
                            key={message.id}
                            message={toDisplayMessage(message)}
                            trainingVideoController={trainingVideoController}
                            projectPlanController={projectPlanController}
                            conversationType={
                                conversation.conversation_type as LoriConversationType
                            }
                        />
                    ))}
                </Container>
            ) : (
                <Typography variant="body1" color="textSecondary">
                    Select a conversation to view messages
                </Typography>
            )}
        </Container>
    )
}
